import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import BottomNav from "@/components/BottomNav";
import LanguagePersonaBar from "@/components/LanguagePersonaBar";
import CourseDiscovery from "@/components/CourseDiscovery";
import VisionScan from "@/components/VisionScan";
import KeyFeatures from "@/components/KeyFeatures";
import StreakCalendar from "@/components/StreakCalendar";
import FlashcardsDailyCard from "@/components/FlashcardsDailyCard";
import FriendLeaderboard from "@/components/FriendLeaderboard";
import LanguageEntryCard from "@/components/LanguageEntryCard";
import WelcomeVideoCard from "@/components/WelcomeVideoCard";
import QuillMascot from "@/components/QuillMascot";
import { pickQuillLine } from "@/lib/quillLines";
import { api } from "@/lib/api";
import { toast } from "sonner";
import LoadingScreen from "@/components/LoadingScreen";
import CPXSurveyModal from "@/components/CPXSurveyModal";
import EarnCreditsHub from "@/components/EarnCreditsHub";
import { useT } from "@/hooks/useT";
import {
  Search,
  Sparkles,
  Brain,
  Calendar,
  Library as LibraryIcon,
  Trophy,
  UserPlus,
  Gamepad2,
  Blend,
  Languages,
  ArrowRight,
  Coins,
  Layers,
} from "lucide-react";

const QUICK_TOPICS = [
  "Master macroeconomics in Hindi",
  "Beherrsche Quantenphysik · 30 Tage",
  "Maîtriser la finance de marché",
  "தமிழில் IoT-ஐ தேர்ச்சி பெற",
  "Learn derivatives for JEE Advanced",
];

export default function Hub() {
  const { user, initialSessionFetched } = useAuth();
  const navigate = useNavigate();
  const { t } = useT();
  const [goal, setGoal] = useState("");
  const [credits, setCredits] = useState(0);
  const [isLimitless, setIsLimitless] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [adOpen, setAdOpen] = useState(false);
  // Difficulty tier selector — drives both the prompt depth (Basic /
  // Intermediate / Advanced) and the credit cost (1 / 2 / 3). Pricing is
  // fetched once from /counselor/path-pricing so the matrix lives only
  // on the server.
  const [tier, setTier] = useState("intermediate");
  const [tiers, setTiers] = useState([
    { id: "basic", label: "Basic", credits: 1, blurb: "Real-world analogies · plain language" },
    { id: "intermediate", label: "Intermediate", credits: 2, blurb: "Balanced depth · working knowledge assumed" },
    { id: "expert", label: "Advanced", credits: 3, blurb: "Executive Summary · Strategic ROI · frameworks by name" },
  ]);
  const tierMeta = tiers.find((tt) => tt.id === tier) || tiers[1];
  const insufficient = !isLimitless && credits < (tierMeta.credits || 1);
  // Quill's hero line — picked once on mount so it doesn't change when
  // the user types into the search box.
  const [quillLine] = useState(() => pickQuillLine("empty_dashboard"));

  useEffect(() => {
    if (initialSessionFetched && !user) navigate("/login", { replace: true });
    if (user) {
      api
        .get("/credits/balance")
        .then(({ data }) => {
          setCredits(data.credits || 0);
          setIsLimitless(!!data.is_limitless);
        })
        .catch(() => {});
    }
  }, [user, initialSessionFetched, navigate]);

  // Pull the tier→credit pricing matrix once. Static fallback already in
  // state so the UI never blocks on this fetch.
  useEffect(() => {
    api.get("/counselor/path-pricing")
      .then(({ data }) => {
        if (Array.isArray(data?.tiers) && data.tiers.length) setTiers(data.tiers);
      })
      .catch(() => {});
  }, []);

  // Listen for the global `oak:open-ad` event so the feature carousel (and
  // any other surface that dispatches it) can pop the Earn Credits Hub
  // without prop-drilling. PathView has the same listener — keep both in
  // sync if the event name ever changes.
  useEffect(() => {
    const onOpenAd = () => setAdOpen(true);
    window.addEventListener("oak:open-ad", onOpenAd);
    return () => window.removeEventListener("oak:open-ad", onOpenAd);
  }, []);

  const submit = async (text) => {
    const g = (text ?? goal).trim();
    if (!g) return;
    if (insufficient) {
      toast.error(`This tier costs ${tierMeta.credits} credits — top up first.`);
      setAdOpen(true);
      return;
    }
    setGenerating(true);
    try {
      const { data } = await api.post("/counselor/path", { goal: g, difficulty_tier: tier });
      toast.success(`${tierMeta.label} path generated · −${tierMeta.credits} credit${tierMeta.credits === 1 ? "" : "s"}`);
      // Optimistic balance refresh so the header updates without a reload.
      api.get("/credits/balance").then(({ data: b }) => setCredits(b.credits || 0)).catch(() => {});
      navigate(`/path/${data.path_id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Generation failed");
    } finally {
      setGenerating(false);
    }
  };

  if (!initialSessionFetched || !user) return <LoadingScreen />;

  return (
    <div className="min-h-screen pb-20 md:pb-0" data-testid="hub-page">
      <Header credits={credits} onOpenAd={() => setAdOpen(true)} />

      <main className="max-w-6xl mx-auto px-6 md:px-10 py-10 md:py-16">
        {/* Hero search */}
        <section className="text-center relative">
          {/* Quill greeter — softly anchors brand recall above the headline */}
          <div className="mb-6 flex justify-center" data-testid="hub-quill-greeter">
            <QuillMascot size="md" speech={quillLine} testid="hub-quill" />
          </div>

          <div className="label-eyebrow inline-flex items-center gap-2 mb-4">
            <span className="h-1.5 w-1.5 rounded-full bg-[#00FF9D] animate-pulse" />
            {t("hub_eyebrow")}
          </div>
          <h1 className="font-display text-4xl sm:text-5xl md:text-7xl tracking-tight leading-[0.98] max-w-4xl mx-auto">
            {t("hub_title_pre")}{" "}
            <span className="bg-gradient-to-r from-[#00FF9D] via-[#0044FF] to-[#FF7B00] bg-clip-text text-transparent">
              {t("hub_title_word")}
            </span>{" "}
            {t("hub_title_post")}
          </h1>
          <p className="mt-5 text-white/55 max-w-xl mx-auto text-[15px]">{t("hub_sub")}</p>

          <form
            onSubmit={(e) => {
              e.preventDefault();
              submit();
            }}
            className="mt-8 max-w-3xl mx-auto"
          >
            <div className="relative glass !rounded-2xl p-1.5 flex flex-col md:flex-row md:items-center gap-2">
              <div className="flex items-center flex-1 gap-2">
                <Search size={18} className="ml-3 text-white/40 shrink-0" />
                <input
                  value={goal}
                  onChange={(e) => setGoal(e.target.value)}
                  placeholder={t("hub_placeholder")}
                  className="flex-1 bg-transparent border-0 px-2 py-4 text-base md:text-lg placeholder:text-white/30 focus:outline-none w-full"
                  data-testid="hub-search-input"
                />
              </div>
              <button
                type="submit"
                disabled={generating || !goal.trim()}
                className="btn-primary rounded-xl px-5 py-3 font-display font-semibold flex items-center justify-center gap-2 disabled:opacity-50 w-full md:w-auto"
                data-testid="hub-generate-btn"
              >
                {generating ? "…" : t("btn_generate")}
                {!isLimitless && (
                  <span
                    className="inline-flex items-center gap-1 text-[10px] font-mono-oak font-semibold bg-white/15 rounded-full px-2 py-0.5 tabular-nums"
                    data-testid="hub-generate-cost"
                    title={`${tierMeta.credits} credit${tierMeta.credits === 1 ? "" : "s"} for ${tierMeta.label}`}
                  >
                    <Coins size={9} /> {tierMeta.credits}
                  </span>
                )}
                <Sparkles size={14} />
              </button>
            </div>
          </form>

          {/* Difficulty tier selector — drives prompt depth (Basic /
              Intermediate / Advanced) AND the credit cost (1/2/3).
              Sits directly under the search input so the choice is
              made before the learner hits Generate. */}
          <div
            className="mt-4 max-w-3xl mx-auto inline-flex flex-wrap items-stretch justify-center gap-2 p-1.5 rounded-2xl border border-white/10 bg-white/[0.03]"
            role="tablist"
            aria-label="Mastery tier"
            data-testid="hub-tier-selector"
          >
            <Layers size={12} className="text-white/40 self-center ml-2" />
            {tiers.map((tt) => {
              const active = tier === tt.id;
              const cantAfford = !isLimitless && credits < tt.credits;
              return (
                <button
                  key={tt.id}
                  type="button"
                  role="tab"
                  aria-selected={active}
                  onClick={() => setTier(tt.id)}
                  className={`relative cursor-pointer px-3 py-2 rounded-xl text-xs font-display font-semibold transition flex flex-col items-start text-left min-w-[140px] ${
                    active
                      ? "bg-[#0044FF]/25 text-white border border-[#0044FF]/55 shadow-[0_4px_20px_-8px_rgba(0,68,255,0.5)]"
                      : "bg-white/[0.04] text-white/80 hover:text-white hover:bg-white/[0.08] border border-white/10 hover:border-white/25"
                  }`}
                  data-testid={`hub-tier-${tt.id}`}
                >
                  <div className="flex items-center justify-between w-full gap-2">
                    <span>{tt.label}</span>
                    <span
                      className={`inline-flex items-center gap-0.5 text-[9px] font-mono-oak rounded-full px-1.5 py-0.5 tabular-nums ${
                        cantAfford
                          ? "bg-rose-500/15 text-rose-200 border border-rose-500/30"
                          : "bg-white/10 text-white/65"
                      }`}
                      data-testid={`hub-tier-${tt.id}-cost`}
                    >
                      <Coins size={8} /> {tt.credits}
                    </span>
                  </div>
                  <span className="text-[9px] text-white/55 font-mono-oak normal-case tracking-normal mt-0.5 leading-snug">
                    {tt.blurb}
                  </span>
                </button>
              );
            })}
          </div>

          {!isLimitless && insufficient && (
            <p
              className="mt-3 text-[11px] text-rose-300 max-w-md mx-auto"
              data-testid="hub-tier-insufficient"
            >
              You need {tierMeta.credits} credit{tierMeta.credits === 1 ? "" : "s"} for {tierMeta.label} but have {credits}.{" "}
              <button
                type="button"
                onClick={() => setAdOpen(true)}
                className="underline underline-offset-2 hover:text-rose-200"
                data-testid="hub-tier-topup"
              >
                Earn credits
              </button>
              {" "}or pick a lower tier.
            </p>
          )}

          <div className="mt-5 flex items-center justify-center flex-wrap gap-2" data-testid="hub-suggestions">
            {QUICK_TOPICS.map((q) => (
              <button
                key={q}
                type="button"
                onClick={() => {
                  // Populate the search input so the user can review/tweak
                  // before hitting Generate. Previously auto-submitted, which
                  // locked every subsequent chip behind the first request.
                  setGoal(q);
                  const el = document.querySelector('[data-testid="hub-search-input"]');
                  if (el) el.focus();
                }}
                className="text-xs px-3 py-1.5 rounded-full border border-white/10 bg-white/[0.03] hover:bg-white/10 text-white/70 hover:text-white transition"
                data-testid={`hub-suggestion-${q.slice(0, 12).replace(/\s+/g, "-").toLowerCase()}`}
              >
                {q}
              </button>
            ))}
          </div>

          {/* Course discovery — guided funnel for learners who don't know
              what to type. Lives between the hero search and the
              language/tone bar so it's the natural next step when the
              search box feels intimidating. */}
          <CourseDiscovery
            busy={generating}
            isFree={isLimitless || credits > 0}
            isLimitless={isLimitless}
            onPickCourse={(goalText) => {
              setGoal(goalText);
              submit(goalText);
            }}
          />

          <div className="mt-8 flex flex-col items-center gap-2">
            {/* Explanatory line — clarifies that the language + tone
                selectors below drive the AI-generated COURSE CONTENT
                (whitepaper text, audio narration, exam questions),
                not the website chrome. */}
            <p
              className="text-[12px] text-white/55 max-w-md text-center leading-relaxed"
              data-testid="lang-persona-help"
            >
              {t("lang_persona_help")}
            </p>
            <LanguagePersonaBar />
          </div>
        </section>

        {/* Welcome video + Feature carousel — side-by-side on desktop,
            stacked on mobile. Combined into one section so the dashboard
            no longer requires a long scroll on phones. The carousel
            auto-rotates through every shippable capability. */}
        <section
          className="mt-12 md:mt-16 grid grid-cols-1 lg:grid-cols-2 gap-5 items-stretch"
          data-testid="hub-welcome-video-section"
        >
          <WelcomeVideoCard />
          <KeyFeatures />
        </section>

        {/* Iter69 — Streak Calendar. Sits below the hero so returning
            users see their streak status as soon as they land, without
            having to open the hamburger. Hidden by render-null for
            brand-new users via the component's own empty-state copy. */}
        <section className="mt-6" data-testid="hub-streak-section">
          <StreakCalendar />
        </section>

        {/* Iter75 / Phase 4 — Daily Flashcards. Self-hides for users
            who haven't generated a deck yet (discovery happens via the
            in-lesson "Make flashcards" CTA). When present, it sits
            right after the streak so the dopamine loop is contiguous. */}
        <section className="mt-4" data-testid="hub-flashcards-section">
          <FlashcardsDailyCard />
        </section>

        {/* Iter76 / P2 — Friend Leaderboard. Self-hides for users with
            no referrals (shows invite CTA instead). XP + streak comparison
            across the user's mutual referral network. */}
        <section className="mt-4" data-testid="hub-leaderboard-section">
          <FriendLeaderboard />
        </section>

        {/* Iter81 — Language Learning entry card. Self-hides nothing —
            always present on the Hub so users discover the gamified
            language mode. Routes to /learn-language. */}
        <section className="mt-4" data-testid="hub-language-section">
          <LanguageEntryCard />
        </section>

        {/* OAK Vision — upload image/PDF to extract a goal */}
        <section className="mt-12 md:mt-16" data-testid="hub-vision-section">
          <VisionScan
            onGoalExtracted={(result) => {
              setGoal(result?.goal || "");
              // Scroll the hero search into view so the prefilled goal is
              // obvious, then focus the input so the user can tweak + Enter.
              const el = document.querySelector('[data-testid="hub-search-input"]');
              if (el) {
                el.scrollIntoView({ behavior: "smooth", block: "center" });
                setTimeout(() => el.focus(), 350);
              }
              toast.success("Goal loaded — review it and hit Generate.");
            }}
          />
        </section>

        {/* Pillars — consistent 2×3 grid with hover scale */}
        <section
          className="mt-20 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5"
          data-testid="hub-pillars"
        >
          <Pillar to="/academy" Icon={Brain} title={t("pillar_academy")} body={t("pillar_academy_body")} glow="glow-cobalt" tLabel={t("open")} />
          <Pillar to="/planner" Icon={Calendar} title={t("pillar_planner")} body={t("pillar_planner_body")} glow="glow-mint" tLabel={t("open")} />
          <Pillar to="/library" Icon={LibraryIcon} title={t("pillar_library")} body={t("pillar_library_body")} tLabel={t("open")} />
          <Pillar to="/referrals" Icon={UserPlus} title={t("pillar_referrals")} body={t("pillar_referrals_body")} tLabel={t("open")} />
          <Pillar to="/library" Icon={Trophy} title={t("pillar_certs")} body={t("pillar_certs_body")} tLabel={t("open")} />
        </section>

        {/* Modes — informational. Tooltip directs users to the AI Academy to actually use these. */}
        <section
          className="mt-16 group/modes relative"
          data-testid="hub-modes"
          title={t("modes_tooltip")}
        >
          <div className="label-eyebrow mb-3 inline-flex items-center gap-2">
            {t("modes_title")}
            <span
              className="text-[10px] font-display uppercase tracking-wider text-[#ffb067] bg-[#ffb067]/10 border border-[#ffb067]/30 rounded-full px-2 py-0.5"
              data-testid="modes-tip-pill"
            >
              Tip
            </span>
          </div>
          <div
            className="pointer-events-none absolute -top-3 left-0 opacity-0 group-hover/modes:opacity-100 transition-opacity duration-200 text-[11px] text-white/70 bg-[#0B0E14]/90 border border-white/10 rounded-lg px-3 py-1.5 shadow-lg z-10"
            data-testid="modes-tooltip"
          >
            {t("modes_tooltip")}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Mode Icon={Languages} title={t("mode_universal")} body={t("mode_universal_body")} />
            <Mode Icon={Gamepad2} title={t("mode_sim")} body={t("mode_sim_body")} />
            <Mode Icon={Blend} title={t("mode_cross")} body={t("mode_cross_body")} />
          </div>
        </section>

        {/* Key Features — moved into the welcome-video-section row above
            (now a self-playing carousel sitting beside the video so the
            dashboard fits on one mobile scroll). */}
      </main>

      <Footer />
      <BottomNav />
      <EarnCreditsHub
        open={adOpen}
        onOpenChange={setAdOpen}
        onRewarded={(c) => setCredits(c)}
        onUpgrade={() => { setAdOpen(false); window.dispatchEvent(new Event("oak:open-upgrade")); }}
      />
    </div>
  );
}

function Pillar({ to, Icon, title, body, glow = "", tLabel = "Open" }) {
  return (
    <a
      href={to}
      className={`glass p-6 ${glow} flex flex-col gap-2 group transition duration-200 hover:-translate-y-1 hover:scale-[1.015] h-full`}
      data-testid={`hub-pillar-${title.toLowerCase().replace(/\s+/g, "-")}`}
    >
      <Icon size={20} className="text-[#00FF9D] group-hover:scale-110 transition" />
      <div className="font-display text-lg md:text-xl tracking-tight">{title}</div>
      <div className="text-white/55 text-sm">{body}</div>
      <div className="mt-auto pt-2 text-xs text-white/40 flex items-center gap-1 group-hover:text-white/80">
        {tLabel} <ArrowRight size={12} />
      </div>
    </a>
  );
}

function Mode({ Icon, title, body }) {
  return (
    <div className="glass p-5 transition duration-200 hover:scale-[1.015] h-full">
      <Icon size={18} className="text-[#ffb067] mb-2" />
      <div className="font-display text-base tracking-tight">{title}</div>
      <div className="text-white/55 text-sm mt-1">{body}</div>
    </div>
  );
}
